import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, MessageSquare, Plus, Edit, Trash2, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Layout from './Layout';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  displayOrder: number;
}

const FAQManagement: React.FC = () => {
  const navigate = useNavigate();
  const { themeClasses, isLightTheme } = useTheme();
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ question: '', answer: '', displayOrder: 0 });

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/faq');
      const sortedFaqs = (response.data.faqs || []).sort((a: FAQItem, b: FAQItem) => 
        a.displayOrder - b.displayOrder
      );
      setFaqs(sortedFaqs);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({ question: '', answer: '', displayOrder: faqs.length });
    setShowAddForm(true);
    setEditingId(null);
  };

  const handleEdit = (faq: FAQItem) => {
    setFormData({
      question: faq.question,
      answer: faq.answer,
      displayOrder: faq.displayOrder
    });
    setEditingId(faq.id);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingId(null);
    setFormData({ question: '', answer: '', displayOrder: 0 });
  };

  const handleSave = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }

    try {
      setIsSaving(true);
      if (editingId) {
        await api.put(`/admin/faq/${editingId}`, formData);
        toast.success('FAQ updated successfully!');
      } else {
        await api.post('/admin/faq', formData);
        toast.success('FAQ added successfully!');
      }
      handleCancel();
      fetchFAQs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Failed to save FAQ');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) {
      return;
    }

    try {
      await api.delete(`/admin/faq/${id}`);
      toast.success('FAQ deleted successfully!');
      fetchFAQs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ');
    }
  };

  if (loading) {
    return (
      <Layout title="FAQ Management" showBackButton onBack={() => navigate('/admin/users')}>
        <div className="p-4">
          <div className={`${themeClasses.card} p-8 text-center`}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
            <p className={`${themeClasses.textSecondary} mt-4`}>Loading FAQs...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="FAQ Management" showBackButton onBack={() => navigate('/admin/users')}>
      {/* Add FAQ button */}
      <div className="p-4 pb-0">
        <button
          onClick={handleAdd}
          className={`${themeClasses.button} px-4 py-2 rounded-lg flex items-center`}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add FAQ
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Add/Edit Form */}
        {showAddForm && (
          <div className={`${themeClasses.card} mb-4 p-4 rounded-lg`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-lg font-semibold ${themeClasses.text}`}>
                {editingId ? 'Edit FAQ' : 'Add New FAQ'}
              </h2>
              <button
                onClick={handleCancel}
                className={`${themeClasses.textSecondary} hover:${themeClasses.text}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Question
                </label>
                <input
                  type="text"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                  placeholder="Enter the question..."
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Answer (Markdown supported)
                </label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  className={`${themeClasses.input} w-full h-32 resize-none rounded-lg px-3 py-2`}
                  placeholder="Enter the answer... (Markdown syntax supported)"
                />
                <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                  💡 Tip: Use Markdown for formatting (lists, bold, links, etc.)
                </p>
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                  className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                  min="0"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancel}
                  className={`${themeClasses.buttonSecondary} px-4 py-2 rounded-lg`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`${themeClasses.button} px-4 py-2 rounded-lg flex items-center ${
                    isSaving ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FAQ List */}
        <div className={`${themeClasses.card} rounded-lg`}>
          <div className="flex items-center mb-4">
            <MessageSquare className={`w-5 h-5 ${themeClasses.textSecondary} mr-2`} />
            <h2 className={`text-lg font-semibold ${themeClasses.text}`}>Frequently Asked Questions</h2>
          </div>
          
          {faqs.length === 0 ? (
            <div className={`text-center py-12 ${themeClasses.textSecondary}`}>
              <p>No FAQs available. Click "Add FAQ" to create one.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {faqs.map((faq) => (
                <div
                  key={faq.id}
                  className={`${themeClasses.border} border rounded-lg p-4`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className={`text-xs ${themeClasses.textSecondary} mr-2`}>
                          Order: {faq.displayOrder}
                        </span>
                      </div>
                      <h3 className={`font-semibold ${themeClasses.text} mb-2`}>
                        {faq.question}
                      </h3>
                      <p className={`${themeClasses.textSecondary} text-sm whitespace-pre-wrap`}>
                        {faq.answer}
                      </p>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleEdit(faq)}
                        className={`${themeClasses.buttonSecondary} p-2 rounded-lg`}
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(faq.id)}
                        className={`${isLightTheme ? 'text-red-600 hover:bg-red-50' : 'text-red-400 hover:bg-red-900/20'} p-2 rounded-lg transition-colors`}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default FAQManagement;

